import { useState, useEffect, useMemo } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firestore';
import { CONTINENTS, BY_NUM, BY_CODE, ALL_COUNTRIES } from '../data/countries';
import './ExploreMap.scss';

const TOTAL_COUNTRIES = ALL_COUNTRIES.length;

const MILESTONES = [
  [0,   'Your adventure awaits ✈️'],
  [1,   'A spark of wanderlust 🌸'],
  [6,   'The world is calling 💕'],
  [16,  'Footprints on the map 🗺️'],
  [31,  'Halfway to everywhere 🌍'],
  [51,  'Seasoned traveller 💖'],
  [71,  'World explorer 🌟'],
  [91,  'Almost seen it all 👑'],
  [100, 'You\'ve conquered the world 💫'],
];

function getMilestone(pct) {
  let msg = MILESTONES[0][1];
  for (const [threshold, label] of MILESTONES) {
    if (pct >= threshold) msg = label;
  }
  return msg;
}

const EXPLORE_DOC = doc(db, 'explore', 'visited');
const GEO_URL     = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json';

const VISITED_FILL = '#f4a5ae';
const DEFAULT_FILL = '#e8c4d6';
const HOVER_FILL   = '#d4a5a5';
const BORDER       = '#fff';

function computeView(visited) {
  const codes     = Object.keys(visited);
  const countries = codes.map(c => BY_CODE[c]).filter(c => c?.centroid);
  if (countries.length === 0) return { center: [0, 20], zoom: 1 };

  const lons = countries.map(c => c.centroid[0]);
  const lats = countries.map(c => c.centroid[1]);

  const minLon = Math.min(...lons), maxLon = Math.max(...lons);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);

  const centerLon = (minLon + maxLon) / 2;
  const centerLat = (minLat + maxLat) / 2;

  const deltaLon = Math.max(maxLon - minLon, 10);
  const zoom     = Math.min(Math.max(1, 300 / (deltaLon * 2)), 8);

  return { center: [centerLon, centerLat], zoom };
}

const ExploreMap = () => {
  const [visited, setVisited]                 = useState({});
  const [loading, setLoading]                 = useState(true);
  const [tooltip, setTooltip]                 = useState({ visible: false, x: 0, y: 0, text: '' });
  const [activeContinent, setActiveContinent] = useState('Asia');

  useEffect(() => {
    const unsub = onSnapshot(EXPLORE_DOC, snap => {
      setVisited(snap.exists() ? (snap.data().visited ?? {}) : {});
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  const toggle = (code) => {
    setVisited(cur => {
      const updated = { ...cur };
      if (updated[code]) delete updated[code];
      else updated[code] = { visitedAt: new Date().toISOString() };
      setDoc(EXPLORE_DOC, { visited: updated });
      return updated;
    });
  };

  const showTip = (e, text) => setTooltip({ visible: true,  x: e.clientX, y: e.clientY, text });
  const moveTip = (e)       => setTooltip(t => ({ ...t, x: e.clientX, y: e.clientY }));
  const hideTip = ()        => setTooltip(t => ({ ...t, visible: false }));

  const { center, zoom } = useMemo(() => computeView(visited), [visited]);

  // Stats
  const totalCountries  = Object.keys(visited).length;
  const totalContinents = new Set(
    Object.entries(CONTINENTS)
      .filter(([, list]) => list.some(c => visited[c.code]))
      .map(([cont]) => cont)
  ).size;

  const globalPct = Math.round((totalCountries / TOTAL_COUNTRIES) * 100);
  const milestone = getMilestone(globalPct);

  const MAIN_CONTINENTS = ['Asia', 'Europe', 'Americas', 'Africa', 'Oceania'];
  const continentProgress = MAIN_CONTINENTS.map(name => {
    const list  = CONTINENTS[name] ?? [];
    const count = list.filter(c => visited[c.code]).length;
    return { name, count, total: list.length, pct: Math.round((count / list.length) * 100) };
  });

  const visitedInContinent   = (CONTINENTS[activeContinent] ?? []).filter(c => visited[c.code]);
  const continentsWithVisits = Object.entries(CONTINENTS)
    .map(([name, list]) => ({ name, count: list.filter(c => visited[c.code]).length }));

  return (
    <div className="explore-map">

      <div className="world-map-wrap">
        <ComposableMap
          projection="geoEquirectangular"
          projectionConfig={{ scale: 153, rotate: [-11, 0, 0] }}
          style={{ width: '100%', height: '100%' }}
        >
          <ZoomableGroup center={center} zoom={zoom} minZoom={1} maxZoom={10}>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map(geo => {
                  const country = BY_NUM[Number(geo.id)];
                  const isVis   = country ? !!visited[country.code] : false;

                  // Countries not in our tracking list — render same color but non-interactive
                  if (!country) {
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        style={{
                          default:  { fill: DEFAULT_FILL, stroke: BORDER, strokeWidth: 0.5, outline: 'none' },
                          hover:    { fill: DEFAULT_FILL, stroke: BORDER, strokeWidth: 0.5, outline: 'none' },
                          pressed:  { fill: DEFAULT_FILL, stroke: BORDER, strokeWidth: 0.5, outline: 'none' },
                        }}
                      />
                    );
                  }

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onClick={() => toggle(country.code)}
                      onMouseEnter={(e) => showTip(e, `${country.flag} ${country.name}${isVis ? ' ✓' : ''}`)}
                      onMouseMove={moveTip}
                      onMouseLeave={hideTip}
                      style={{
                        default:  { fill: isVis ? VISITED_FILL : DEFAULT_FILL, stroke: BORDER, strokeWidth: 0.5, outline: 'none' },
                        hover:    { fill: HOVER_FILL, stroke: BORDER, strokeWidth: 0.5, outline: 'none', cursor: 'pointer' },
                        pressed:  { fill: VISITED_FILL, stroke: BORDER, strokeWidth: 0.5, outline: 'none' },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>

        {tooltip.visible && (
          <div className="rsm-tooltip" style={{ left: tooltip.x + 12, top: tooltip.y - 28 }}>
            {tooltip.text}
          </div>
        )}

        <p className="map-hint">Click a country to mark it as visited</p>
      </div>

      {/* ── Progress section ── */}
      <div className="travel-progress">
        <div className="progress-header">
          <span className="progress-milestone">{milestone}</span>
          <span className="progress-fraction">{totalCountries} <span className="progress-total">/ {TOTAL_COUNTRIES}</span></span>
        </div>
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${Math.max(globalPct, globalPct > 0 ? 2 : 0)}%` }}>
            {globalPct > 8 && <span className="progress-pct">{globalPct}%</span>}
          </div>
        </div>
        {globalPct > 0 && globalPct <= 8 && (
          <span className="progress-pct-outside">{globalPct}%</span>
        )}

        <div className="continent-bars">
          {continentProgress.map(({ name, count, total, pct }) => (
            <div key={name} className="cbar-row">
              <span className="cbar-label">{name}</span>
              <div className="cbar-track">
                <div className="cbar-fill" style={{ width: `${pct}%` }} />
              </div>
              <span className="cbar-count">{count}<span className="cbar-total">/{total}</span></span>
            </div>
          ))}
        </div>
      </div>

      <div className="explore-stats">
        {[
          { num: totalCountries,  label: 'Countries'  },
          { num: totalContinents, label: 'Continents' },
        ].map(s => (
          <div key={s.label} className="stat">
            <span className="stat-number">{s.num}</span>
            <span className="stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {totalCountries > 0 && (
        <>
          <div className="continent-tabs">
            {continentsWithVisits.map(({ name, count }) => (
              <button
                key={name}
                className={`continent-tab ${activeContinent === name ? 'active' : ''} ${count === 0 ? 'empty' : ''}`}
                onClick={() => setActiveContinent(name)}
              >
                {name}
                {count > 0 && <span className="tab-count">{count}</span>}
              </button>
            ))}
          </div>

          {visitedInContinent.length === 0 ? (
            <p className="no-visits-here">No countries visited in {activeContinent} yet.</p>
          ) : (
            <div className="visited-list">
              {visitedInContinent.map(country => (
                <div key={country.code} className="visited-item">
                  <span className="vi-flag">{country.flag}</span>
                  <span className="vi-name">{country.name}</span>
                  <button className="vi-remove" onClick={() => toggle(country.code)}>×</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {totalCountries === 0 && !loading && (
        <p className="no-visits-msg">Click any country on the map to start tracking your travels!</p>
      )}
    </div>
  );
};

export default ExploreMap;
